import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  UserPlus,
  Lightning,
  ArrowsClockwise,
  FileText,
  VideoCamera,
  ShareNetwork,
  Warning,
  Crown,
} from "@phosphor-icons/react";
import { api, formatNum } from "@/lib/gado";

function Kpi({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className="rounded-xl border border-gado-line bg-white p-4 min-w-0" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`h-7 w-7 rounded-lg grid place-items-center shrink-0 ${accent ? "bg-gado-gold/15 text-gado-gold" : "bg-gado-green/10 text-gado-green"}`}>
          <Icon size={15} weight="fill" />
        </span>
        <p className="text-[10px] uppercase tracking-[0.14em] text-gado-muted font-semibold truncate">{label}</p>
      </div>
      <p className="font-cabinet text-xl md:text-2xl font-extrabold text-gado-ink tabular-nums truncate">{value}</p>
      {sub && <p className="text-[11px] text-gado-muted mt-0.5 truncate">{sub}</p>}
    </div>
  );
}

function UsageRow({ icon: Icon, label, month, total, testid }) {
  const max = Math.max(total, 1);
  const pct = Math.max(4, Math.round((month / max) * 100));
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <span className="h-8 w-8 rounded-lg bg-gado-green/10 text-gado-green grid place-items-center shrink-0">
        <Icon size={16} weight="fill" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between gap-2 text-xs">
          <span className="font-semibold text-gado-ink truncate">{label}</span>
          <span className="text-gado-muted tabular-nums shrink-0">
            {formatNum(month, 0)} no mês · {formatNum(total, 0)} total
          </span>
        </div>
        <div className="mt-1 h-1.5 rounded-full bg-gado-line/60 overflow-hidden">
          <div className="h-full rounded-full bg-gado-green" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}

function ChurnList({ title, icon: Icon, items, empty, renderSub, testid }) {
  return (
    <div className="rounded-xl border border-gado-line bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <span className="h-7 w-7 rounded-lg bg-destructive/10 text-destructive grid place-items-center">
          <Icon size={15} weight="fill" />
        </span>
        <h3 className="font-cabinet font-bold text-gado-ink text-sm">{title}</h3>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-gado-muted">{empty}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((u) => (
            <li key={u.user_id} className="flex items-center justify-between gap-2 text-xs">
              <Link
                to={`/admin/usuarios?q=${encodeURIComponent(u.email || "")}`}
                className="min-w-0 truncate text-gado-ink font-medium hover:text-gado-green hover:underline"
                data-testid={`${testid}-user-${u.user_id}`}
              >
                {u.name || u.email}
              </Link>
              <span className="shrink-0 text-gado-muted tabular-nums">{renderSub(u)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminAnalytics() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-analytics"],
    queryFn: async () => (await api.get("/admin/analytics")).data,
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-5" data-testid="admin-analytics-page">
        <h1 className="font-cabinet text-2xl md:text-3xl font-bold text-gado-ink tracking-tight">Analytics</h1>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => <div key={i} className="h-24 rounded-xl bg-gado-line/40 animate-pulse" />)}
        </div>
      </div>
    );
  }

  const { kpis, cohorts, feature_usage, churn_risk } = data;

  return (
    <div className="space-y-5" data-testid="admin-analytics-page">
      <div>
        <h1 className="font-cabinet text-2xl md:text-3xl font-bold text-gado-ink tracking-tight">Analytics</h1>
        <p className="text-gado-muted text-sm">Crescimento, retenção, uso de recursos e risco de churn — dados reais.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi icon={UserPlus} label="Cadastros no mês" value={formatNum(kpis.signups_month, 0)} sub="novas contas" testid="ana-signups" />
        <Kpi icon={Lightning} label="Ativação geral" value={`${kpis.activation_total_pct}%`} sub="fizeram ≥1 pesagem" accent testid="ana-activation" />
        <Kpi icon={ArrowsClockwise} label="Retenção 30d" value={`${kpis.retention_30d_avg_pct}%`} sub="média dos coortes" testid="ana-retention" />
        <Kpi icon={FileText} label="PDFs no mês" value={formatNum(kpis.pdfs_month, 0)} sub="gerados pelos usuários" testid="ana-pdfs" />
        <Kpi icon={VideoCamera} label="Análises IA no mês" value={formatNum(kpis.ia_month, 0)} sub="vídeos processados" accent testid="ana-ia" />
        <Kpi icon={ShareNetwork} label="Views de links no mês" value={formatNum(kpis.shares_month, 0)} sub="aberturas de links públicos" testid="ana-shares" />
      </div>

      {/* Coortes */}
      <div className="rounded-xl border border-gado-line bg-white p-4" data-testid="ana-cohorts">
        <h3 className="font-cabinet font-bold text-gado-ink text-sm mb-3">Coortes — cadastros, ativação e retenção por mês</h3>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={cohorts} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E8EAE6" />
              <XAxis dataKey="mes" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#5C6A60" }} />
              <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#5C6A60" }} allowDecimals={false} />
              <Tooltip
                cursor={{ fill: "#F3F5F1" }}
                formatter={(v, name) => [v, name === "signups" ? "Cadastros" : name]}
                contentStyle={{ borderRadius: 10, border: "1px solid #E8EAE6", fontSize: 12 }}
              />
              <Bar dataKey="signups" fill="#1A472A" radius={[6, 6, 0, 0]} maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gado-muted uppercase tracking-wider text-[10px]">
                <th className="py-1.5 pr-3 font-semibold">Mês</th>
                <th className="py-1.5 pr-3 font-semibold text-right">Cadastros</th>
                <th className="py-1.5 pr-3 font-semibold text-right">Ativação</th>
                <th className="py-1.5 font-semibold text-right">Retenção 30d</th>
              </tr>
            </thead>
            <tbody>
              {cohorts.map((c) => (
                <tr key={c.mes} className="border-t border-gado-line/60" data-testid={`ana-cohort-${c.mes}`}>
                  <td className="py-1.5 pr-3 font-medium text-gado-ink">{c.mes}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{c.signups}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{c.ativacao_pct}%</td>
                  <td className="py-1.5 text-right tabular-nums">{c.retencao_30d_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Uso de recursos */}
      <div className="rounded-xl border border-gado-line bg-white p-4 space-y-3" data-testid="ana-feature-usage">
        <h3 className="font-cabinet font-bold text-gado-ink text-sm">Uso de recursos</h3>
        <UsageRow icon={FileText} label="PDFs gerados" month={feature_usage.pdfs.month} total={feature_usage.pdfs.total} testid="usage-pdfs" />
        <UsageRow icon={VideoCamera} label="Análises de IA" month={feature_usage.analises_ia.month} total={feature_usage.analises_ia.total} testid="usage-ia" />
        <UsageRow icon={ShareNetwork} label="Views de links compartilhados" month={feature_usage.compartilhamentos_views.month} total={feature_usage.compartilhamentos_views.total} testid="usage-shares" />
        <UsageRow icon={Lightning} label="Pesagens registradas" month={feature_usage.pesagens.month} total={feature_usage.pesagens.total} testid="usage-txs" />
      </div>

      {/* Risco de churn */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ChurnList
          title="Pro inativo há 30+ dias"
          icon={Crown}
          items={churn_risk.pro_inactive}
          empty="Nenhum assinante Pro inativo."
          renderSub={(u) => (u.last_login_at ? `últ. login ${new Date(u.last_login_at).toLocaleDateString("pt-BR")}` : "nunca entrou")}
          testid="churn-pro-inactive"
        />
        <ChurnList
          title="Grátis batendo a cota de PDF"
          icon={Warning}
          items={churn_risk.free_capped}
          empty="Nenhum usuário grátis no limite este mês."
          renderSub={(u) => `${u.pdfs_used}/${u.pdfs_limit} PDFs`}
          testid="churn-free-capped"
        />
      </div>
    </div>
  );
}
